'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleDashboardPath } from '@/lib/roleRouting';

import BrandLogo from '@/components/ui/BrandLogo';

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

interface FloatingInputProps {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: string;
  error?: string;
  success?: boolean;
  rightSlot?: React.ReactNode;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

function FloatingInput({
  label, type = 'text', value, onChange, required, autoComplete, error, success, rightSlot, inputMode,
}: FloatingInputProps) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;

  return (
    <div className="relative">
      <div
        className={`relative rounded-2xl transition-all duration-300 ${
          error
            ? 'ring-2 ring-red-400/60'
            : success
            ? 'ring-2 ring-emerald-400/60'
            : focused
            ? 'ring-2 ring-[#F4C542]/60'
            : 'ring-1 ring-white/10'
        }`}
        style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: focused
            ? '0 0 0 4px rgba(244,197,66,0.08), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <label
          className="absolute left-4 transition-all duration-200 pointer-events-none select-none font-medium"
          style={{
            top: lifted ? '8px' : '50%',
            transform: lifted ? 'translateY(0) scale(0.75)' : 'translateY(-50%) scale(1)',
            transformOrigin: 'left center',
            color: error ? '#f87171' : success ? '#34d399' : focused ? '#F4C542' : 'rgba(255,255,255,0.45)',
            fontSize: '15px',
            zIndex: 1,
          }}
        >
          {label}{required && ' *'}
        </label>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete={autoComplete}
          inputMode={inputMode}
          className="w-full bg-transparent outline-none text-white text-[15px] font-medium"
          style={{
            padding: lifted ? '26px 48px 10px 16px' : '18px 48px 18px 16px',
            paddingRight: rightSlot ? '48px' : '16px',
          }}
          aria-label={label}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">{rightSlot}</div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 ml-1 text-red-400 text-xs flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
          {error}
        </p>
      )}
      {success && !error && (
        <p className="mt-1.5 ml-1 text-emerald-400 text-xs flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round"/></svg>
          Looks good
        </p>
      )}
    </div>
  );
}

function LoginForm() {
  const { signIn, user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '';
  const urlError = searchParams.get('error');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(urlError || '');
  const [mounted, setMounted] = useState(false);
  const redirectedRef = useRef(false);

  const [touched, setTouched] = useState({ email: false, password: false });

  useEffect(() => {
    setMounted(true);
  }, []);

  // Single redirect effect — fires once when auth resolves with a user
  useEffect(() => {
    if (!authLoading && user && !redirectedRef.current) {
      redirectedRef.current = true;
      // Prefer DB profile role over user_metadata role for accurate routing
      const role = user?.user_metadata?.role;
      const destination =
        redirectTo &&
        !redirectTo.startsWith('/dashboard') &&
        !redirectTo.startsWith('/admin') &&
        !redirectTo.startsWith('/guide') &&
        !redirectTo.startsWith('/hotel-owner') &&
        !redirectTo.startsWith('/vehicle-owner') &&
        !redirectTo.startsWith('/agency')
          ? redirectTo
          : getRoleDashboardPath(role);
      router.replace(destination);
    }
  }, [user, authLoading, router, redirectTo]);

  useEffect(() => {
    if (urlError) {
      setError(decodeURIComponent(urlError));
    }
  }, [urlError]);

  const emailError = touched.email && email && !isValidEmail(email) ? 'Enter a valid email address' : '';
  const emailSuccess = touched.email && email.length > 0 && isValidEmail(email);
  const passwordError = touched.password && password && password.length < 6 ? 'Password must be at least 6 characters' : '';
  const passwordSuccess = touched.password && password.length >= 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTouched({ email: true, password: true });
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!isValidEmail(email)) { setError('Please enter a valid email address.'); return; }
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    try {
      // signIn triggers onAuthStateChange → sets user → useEffect above handles redirect.
      // Do NOT call router.replace() here — that causes a double redirect race condition.
      await signIn(email, password, rememberMe);
      // Keep loading=true; the redirect effect will navigate away.
      // If signIn throws, the catch block resets loading.
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
      setLoading(false);
    }
  };

  // Show loading screen while auth state is being determined
  if (authLoading || (user && !redirectedRef.current)) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0B2348 0%, #0d1f3c 50%, #091a35 100%)' }}
        aria-label="Loading"
      >
        <div className="w-8 h-8 border-2 border-[#F4C542] border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading...</span>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-5 py-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0B2348 0%, #0d1f3c 50%, #091a35 100%)' }}
    >
      {/* Ambient glow orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(244,197,66,0.18) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.15) 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(244,197,66,0.2) 0%, transparent 70%)' }}
        />
      </div>

      <div
        className="w-full max-w-md relative"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <BrandLogo size={52} showTagline={true} asLink={true} textColor="light" glow={true} />
          </div>
          <h1 className="text-white font-bold text-3xl mb-1.5 tracking-tight">Welcome back</h1>
          <p className="text-white/50 text-sm">Sign in to continue your Nepal journey</p>
          {redirectTo !== '/dashboard' && (
            <p className="text-[#F4C542]/70 text-xs mt-2">Sign in to access this page</p>
          )}
        </div>

        {/* Animated Stepper */}
        <div className="flex items-center justify-center gap-0 mb-8 px-4">
          {/* Step 1 — Sign In (active) */}
          <div className="flex flex-col items-center">
            <div className="relative flex items-center justify-center">
              {/* Pulse ring */}
              <div
                className="absolute w-10 h-10 rounded-full animate-ping"
                style={{ background: 'rgba(244,197,66,0.15)', animationDuration: '2s' }}
              />
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm relative z-10"
                style={{
                  background: 'linear-gradient(135deg, #F4C542, #e6b830)',
                  boxShadow: '0 0 20px rgba(244,197,66,0.5), 0 4px 12px rgba(244,197,66,0.3)',
                  color: '#0B2348',
                }}
              >
                1
              </div>
            </div>
            <span
              className="text-xs font-semibold mt-2"
              style={{ color: '#F4C542' }}
            >
              Sign In
            </span>
          </div>

          {/* Connector */}
          <div
            className="h-px flex-1 mx-3 mb-5"
            style={{ background: 'linear-gradient(90deg, rgba(244,197,66,0.4), rgba(255,255,255,0.08))' }}
          />

          {/* Step 2 — Dashboard (glass) */}
          <div className="flex flex-col items-center">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
              style={{
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.35)',
              }}
            >
              2
            </div>
            <span className="text-xs font-medium mt-2 text-white/30">Dashboard</span>
          </div>
        </div>

        {/* Glass Card */}
        <div
          className="rounded-3xl p-7"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Email */}
            <FloatingInput
              label="Email Address"
              type="email"
              value={email}
              onChange={(v) => {
                setEmail(v);
                if (!touched.email) setTouched(t => ({ ...t, email: true }));
              }}
              required
              autoComplete="email"
              error={emailError}
              success={emailSuccess}
              inputMode="email"
            />

            {/* Password */}
            <FloatingInput
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(v) => {
                setPassword(v);
                if (!touched.password) setTouched(t => ({ ...t, password: true }));
              }}
              required
              autoComplete="current-password"
              error={passwordError}
              success={passwordSuccess}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-white/40 hover:text-white/80 transition-colors p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" strokeLinecap="round"/>
                      <path d="M1 1l22 22" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              }
            />

            {/* Forgot password link */}
            <div className="flex justify-end -mt-1">
              <Link
                href="/forgot-password"
                className="text-[#F4C542] text-xs hover:underline font-medium transition-opacity hover:opacity-80"
              >
                Forgot password?
              </Link>
            </div>

            {/* Remember Me */}
            <label className="flex items-center gap-3 cursor-pointer group mt-1">
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
                  rememberMe
                    ? 'border-[#F4C542]'
                    : 'border-white/25 group-hover:border-white/40'
                }`}
                style={rememberMe ? {
                  background: 'linear-gradient(135deg, #F4C542, #e6b830)',
                  boxShadow: '0 0 8px rgba(244,197,66,0.4)',
                } : { background: 'rgba(255,255,255,0.04)' }}
                onClick={() => setRememberMe(!rememberMe)}
              >
                {rememberMe && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0B2348" strokeWidth="3.5">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="sr-only" />
              <span className="text-white/55 text-sm">Remember me for 30 days</span>
            </label>

            {/* Error */}
            {error && (
              <div
                className="rounded-2xl px-4 py-3 text-red-400 text-sm flex items-start gap-2"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                </svg>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl text-sm font-bold text-[#0B2348] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 mt-2"
              style={{
                background: loading ? 'rgba(244,197,66,0.5)' : 'linear-gradient(135deg, #F4C542, #e6b830)',
                boxShadow: loading ? 'none' : '0 8px 24px rgba(244,197,66,0.35), 0 2px 8px rgba(244,197,66,0.2)',
              }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#0B2348]/40 border-t-[#0B2348] rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <span className="text-white/25 text-xs font-medium">or</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Sign up link */}
          <p className="text-center text-white/45 text-sm">
            Don&apos;t have an account?{' '}
            <Link
              href="/register"
              className="font-semibold hover:underline transition-opacity hover:opacity-80"
              style={{ color: '#F4C542' }}
            >
              Create one free
            </Link>
          </p>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link
            href="/home"
            className="text-white/25 text-xs hover:text-white/50 transition-colors inline-flex items-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0B2348 0%, #0d1f3c 50%, #091a35 100%)' }}
      >
        <div className="w-8 h-8 border-2 border-[#F4C542] border-t-transparent rounded-full animate-spin" />
      </main>
    }>
      <LoginForm />
    </Suspense>
  );
}
